import { computed, h, watch } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { buildSlaughterWeeklyBins } from '@/composables/records/useSlaughtersChartOptions';
import { locationFacetValue, locationLabel } from '@/composables/records/recordsFormat';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, useSlaughters } from '@/plugins/store/actions/queries/slaughters';
import { RECORD_ACTIONS_COLUMN_ID, type SlaughterRow } from '@/types';
import { columnPresence, defaultColumnVisible, isColumnAvailable, TOGGLE_COLUMN_IDS, type SlaughterToggleColumnId } from './useSlaughtersColumnVisibility';

const HEADINGS_KEY = 'views.pigs.records.table.headings';

/** The bulk slaughter offered above the table: one slaughter from a barn divided into its sections (the slaughter form, in bulk). */
export const BULK_CREATE = {
    formId: 2,
    labelKey: 'views.pigs.slaughters.table.createButton',
} as const;

/** Same placeholder `RecordsTable`'s own footer uses for a figure with nothing to average. */
const NO_TOTAL_VALUE = '—';

/** i18n key for each toggleable column's heading, read off the same shared `records` namespace
 * every other records page's headings live under. */
const HEADING_KEYS: Record<SlaughterToggleColumnId, string> = {
    total_live_weight: `${HEADINGS_KEY}.totalLiveWeight`,
    avg_live_weight: `${HEADINGS_KEY}.avgLiveWeight`,
    total_slaughter_weight: `${HEADINGS_KEY}.totalSlaughterWeight`,
    avg_slaughter_weight: `${HEADINGS_KEY}.avgSlaughterWeight`,
    avg_meat_percent: `${HEADINGS_KEY}.avgMeatPercentage`,
    discarded_pigs: `${HEADINGS_KEY}.discardedPigs`,
    avg_discarded_weight: `${HEADINGS_KEY}.avgDiscardedWeight`,
    payment_weight: `${HEADINGS_KEY}.paymentWeight`,
    section: `${HEADINGS_KEY}.section`,
    customer: `${HEADINGS_KEY}.customer`,
    invoice: `${HEADINGS_KEY}.invoiceNo`,
    classification: `${HEADINGS_KEY}.classification`,
    settlement_no: `${HEADINGS_KEY}.settlementNumber`,
    supplier_no: `${HEADINGS_KEY}.supplierNumber`,
    created_by: `${HEADINGS_KEY}.createdBy`,
};

/** What the Slaughters page filters on: the Location column, faceted on the barn id the same way
 * Sold's own Location column is (a name repeats across farms, an id does not), plus the six
 * presence-driven facts - like Sold's own Section facet and Deaths' cause/category, a string-valued
 * fact falls back to the numeric blank sentinel the same way theirs do. */
function facets(): RecordFacetDefinitions<SlaughterRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        barn: {
            value: (row) => locationFacetValue(row.farm, row.barn),
            label: (row) => locationLabel(row.farm, row.barn) || blank,
        },
        section: {
            value: (row) => row.section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.section?.name ?? blank,
        },
        customer: {
            value: (row) => row.customer ?? BLANK_FACET_VALUE,
            label: (row) => row.customer ?? blank,
        },
        invoice: {
            value: (row) => row.invoice ?? BLANK_FACET_VALUE,
            label: (row) => row.invoice ?? blank,
        },
        classification: {
            value: (row) => row.classification ?? BLANK_FACET_VALUE,
            label: (row) => row.classification ?? blank,
        },
        settlement_no: {
            value: (row) => row.settlement_no ?? BLANK_FACET_VALUE,
            label: (row) => row.settlement_no ?? blank,
        },
        supplier_no: {
            value: (row) => row.supplier_no ?? BLANK_FACET_VALUE,
            label: (row) => row.supplier_no ?? blank,
        },
    };
}

/**
 * Table-header-only line break for a heading long enough to overflow its column ("Total slaughter
 * weight (kg)"), split at the word boundary nearest the middle. Table-only by construction: the
 * column-visibility dropdown renders the plain `i18n.global.t(...)` string via `HEADING_KEYS`
 * directly (see `columnToggleOptions`), never through this helper, and its own item already forces
 * `whitespace-nowrap truncate` regardless.
 *
 * `minWords` defaults to 3 - a two-word heading otherwise reads as narrow enough to sit on one line
 * - but "Discarded pigs" is narrower than its neighbours' two-line headers even at two words, so its
 * own call site below passes 2 to force the same split there.
 */
function headerLabel(text: string, minWords = 3): string | (() => ReturnType<typeof h>) {
    const words = text.split(' ');

    if (words.length < minWords) {
        return text;
    }

    const splitAt = Math.ceil(words.length / 2);
    const line1 = words.slice(0, splitAt).join(' ');
    const line2 = words.slice(splitAt).join(' ');

    // Two `whitespace-nowrap` lines stacked in a column, not one `whitespace-pre-line` span: the
    // column is narrower than either half, so a plain preserved newline still re-wraps each half
    // across the space it can't fit, giving three lines instead of the two intended here.
    return () => h('span', { class: 'flex flex-col leading-tight' }, [h('span', { class: 'whitespace-nowrap' }, line1), h('span', { class: 'whitespace-nowrap' }, line2)]);
}

/**
 * The columns, in the order a slaughter reads: the pick-up date, where it happened and how many
 * pigs, then whichever of the mutable weight/discarded/fact columns the reader currently has on.
 *
 * Every mutable column here is either sortable (the weight/discarded/meat-percentage measures, via
 * `accessorKey`) or filterable (Section/Customer/Invoice/Classification/Settlement no./Supplier no.,
 * via `meta.filterable`) - never both, the same split every other records page's own filterable
 * columns already draw.
 */
function columns(isVisible: (columnId: SlaughterToggleColumnId) => boolean): ColumnDef<SlaughterRow>[] {
    const base: ColumnDef<SlaughterRow>[] = [
        { id: 'date', accessorKey: 'date', header: headerLabel(i18n.global.t(`${HEADINGS_KEY}.pickupDate`)), enableSorting: true },
        // `barn`, not an ad hoc column id: `RecordsTable`'s shared `cellText`/`LOCATION_COLUMN_IDS`
        // already render a Farm/Barn chain and give it a width floor under this exact id (see Sold).
        { id: 'barn', header: headerLabel(i18n.global.t(`${HEADINGS_KEY}.location`)), meta: { filterable: true } },
    ];

    // Right after Location, not lumped in with the other filterable facts below: Section is a
    // refinement of where the slaughter happened, so it reads next to the column it narrows.
    if (isVisible('section')) {
        base.push({ id: 'section', header: headerLabel(i18n.global.t(HEADING_KEYS.section)), meta: { filterable: true } });
    }

    // Its own `pigs` heading rather than the shared `.number` every other records page's
    // identically-shaped column uses - a dedicated column, not a rename of the shared one.
    base.push({ id: 'number', accessorKey: 'number', header: headerLabel(i18n.global.t(`${HEADINGS_KEY}.pigs`)), enableSorting: true, meta: { align: 'right' } });

    const sortableWeightColumns: SlaughterToggleColumnId[] = [
        'total_live_weight',
        'avg_live_weight',
        'total_slaughter_weight',
        'avg_slaughter_weight',
        'avg_meat_percent',
        'discarded_pigs',
        'avg_discarded_weight',
        'payment_weight',
    ];

    for (const columnId of sortableWeightColumns) {
        if (isVisible(columnId)) {
            const minWords = columnId === 'discarded_pigs' ? 2 : 3;

            base.push({ id: columnId, accessorKey: columnId, header: headerLabel(i18n.global.t(HEADING_KEYS[columnId]), minWords), enableSorting: true, meta: { align: 'right' } });
        }
    }

    const filterableColumns: SlaughterToggleColumnId[] = ['customer', 'invoice', 'classification', 'settlement_no', 'supplier_no'];

    for (const columnId of filterableColumns) {
        if (isVisible(columnId)) {
            base.push({ id: columnId, header: headerLabel(i18n.global.t(HEADING_KEYS[columnId])), meta: { filterable: true } });
        }
    }

    if (isVisible('created_by')) {
        base.push({ id: 'created_by', accessorKey: 'created_by', header: headerLabel(i18n.global.t(HEADING_KEYS.created_by)), enableSorting: true });
    }

    return [...base, { id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }];
}

/**
 * The Slaughters page's configuration of the shared records page, plus what neither the shared kit
 * nor `RecordsTable` provide on their own: the dual-measure weekly bins its dedicated chart draws,
 * and the column-visibility control over its mutable columns.
 *
 * Column visibility resolves in one fixed order - see `useColumnVisibilityOverrides` - availability first
 * (the four hard-gated facts only once the rows carry them; every other column always available),
 * then the reader's own stored choice, else a presence-aware default (on once a loaded row actually
 * carries it; Created by is always offered and always defaults off). Both availability and the default read the whole loaded set (`page.rows`), not the
 * filtered table rows: narrowing the table to one week must not make a column disappear or flip its
 * default out from under the reader.
 */
export function useSlaughtersPage() {
    const page = useRecordsPage<SlaughterRow>({
        records: { use: useSlaughters, rootKey: queryKeys.getSlaughtersRoot },
        facets,
    });

    const presence = computed(() => columnPresence(page.rows.value));

    /**
     * The filterable mutable columns - the only ones a stale `columnFilters` entry can affect once
     * its column disappears (the weight/discarded pairs are sortable, never filterable).
     */
    const FILTERABLE_COLUMN_IDS: readonly SlaughterToggleColumnId[] = ['section', 'customer', 'invoice', 'classification', 'settlement_no', 'supplier_no'];

    /**
     * Drops a facet's own active filter selection once its column is no longer on the table - a
     * hidden column has no chip left to clear it from, and `applyFacetFilters` would otherwise keep
     * matching rows against a filter the reader can no longer see or reach.
     */
    function clearFilterIfHidden(id: SlaughterToggleColumnId) {
        if (FILTERABLE_COLUMN_IDS.includes(id) && id in page.columnFilters.value) {
            const next = { ...page.columnFilters.value };

            delete next[id];
            page.columnFilters.value = next;
        }
    }

    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<SlaughterToggleColumnId>({
        storageKey: 'pigs.slaughters.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: (id) => isColumnAvailable(id, presence.value),
        defaultVisible: (id) => defaultColumnVisible(id, presence.value),
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
        onHide: clearFilterIfHidden,
    });

    // A period change (or any other change to the loaded set) can drop a filterable column's only
    // data, hiding it - whether by dropping its hard-gated availability or, for a presence-defaulted
    // column with no stored override, by flipping its own default - and taking its facet option away
    // with it, not just a user's own toggle. Prune both the same way.
    watch(presence, () => {
        for (const id of FILTERABLE_COLUMN_IDS) {
            if (!isVisible(id)) {
                clearFilterIfHidden(id);
            }
        }
    });

    const bins = computed(() => buildSlaughterWeeklyBins(page.rows.value));

    /**
     * The table's footer row, via `RecordsTable`'s `totalsSubheader` escape hatch rather than its
     * `totals` prop: `SlaughterRow.total_weight`/`avg_weight` are always null (see `mapSlaughterRow`
     * - a slaughter has two independent weight pairs, never the one figure `totals` sums), so the
     * shared per-row totals cannot express this page's footer at all.
     *
     * Over `visibleRows` (the week/facet-filtered set, matching every other records page's own
     * totals scope). Each average is `Sum(total) / Sum(pigs)` over the rows that actually carry that
     * total, the same weighted-average-over-weighed-rows-only shape `useRecordsAggregation.
     * computeTotals` uses for the shared pair - never a mean of the rows' own per-row averages, which
     * would misstate the true per-pig figure once rows of different sizes mix. Only currently-visible
     * mutable columns get an entry; a hidden column's slot is never rendered, so a stray total for it
     * would go nowhere.
     */
    const totalsSubheader = computed<Record<string, string>>(() => {
        const rows = page.visibleRows.value;
        const decimal = (value: number) => i18n.global.n(value, 'decimal');
        const sum = (pick: (row: SlaughterRow) => number | null) => rows.reduce((total, row) => total + (pick(row) ?? 0), 0);

        /**
         * `Sum(totalPick) / Sum(weightPick)` over rows where `totalPick` is present; null with none.
         * `incomplete` is true whenever at least one row in scope has no `totalPick` at all - the
         * same case the shared `RecordsTable` footer marks with `INCOMPLETE_MARKER` for its own pair
         * (see `useRecordsAggregation.computeTotals`'s `hasRowsWithoutWeight`), reused here as a
         * plain `*` suffix since this footer is plain strings, not that component's own marked slot.
         */
        function weightedAverage(totalPick: (row: SlaughterRow) => number | null, weightPick: (row: SlaughterRow) => number | null): { value: number | null; incomplete: boolean } {
            let totalSum = 0;
            let weightSum = 0;
            let incomplete = false;

            for (const row of rows) {
                const total = totalPick(row);

                if (total !== null) {
                    totalSum += total;
                    weightSum += weightPick(row) ?? 0;
                } else {
                    incomplete = true;
                }
            }

            return { value: weightSum > 0 ? totalSum / weightSum : null, incomplete };
        }

        function averageText(totalPick: (row: SlaughterRow) => number | null, weightPick: (row: SlaughterRow) => number | null): string {
            const { value, incomplete } = weightedAverage(totalPick, weightPick);

            if (value === null) {
                return NO_TOTAL_VALUE;
            }

            return incomplete ? `${decimal(value)} *` : decimal(value);
        }

        const entries: Record<string, string> = {
            date: i18n.global.t('views.pigs.records.table.totals.total'),
            number: decimal(sum((row) => row.number)),
        };

        if (isVisible('total_live_weight')) entries.total_live_weight = decimal(sum((row) => row.total_live_weight));
        if (isVisible('total_slaughter_weight')) entries.total_slaughter_weight = decimal(sum((row) => row.total_slaughter_weight));
        if (isVisible('discarded_pigs')) entries.discarded_pigs = decimal(sum((row) => row.discarded_pigs));
        if (isVisible('payment_weight')) entries.payment_weight = decimal(sum((row) => row.payment_weight));

        if (isVisible('avg_live_weight')) {
            entries.avg_live_weight = averageText(
                (row) => row.total_live_weight,
                (row) => row.number
            );
        }

        if (isVisible('avg_slaughter_weight')) {
            entries.avg_slaughter_weight = averageText(
                (row) => row.total_slaughter_weight,
                (row) => row.number
            );
        }

        if (isVisible('avg_meat_percent')) {
            // A row's own meat-percentage total, backed out of its average - the wire gives the
            // average per row, never a total, so this is the only way to weigh it by pig count,
            // the same shape `avg_discarded_weight` below needs. Null exactly when the slaughter
            // is not yet settled, which drops the row from both sides of the division rather than
            // pulling the figure towards zero - the incomplete case the averages around it flag
            // with `*`.
            entries.avg_meat_percent = averageText(
                (row) => (row.avg_meat_percent === null ? null : row.avg_meat_percent * row.number),
                (row) => row.number
            );
        }

        if (isVisible('avg_discarded_weight')) {
            // A row's own discarded weight total, back out of its average - the wire gives the
            // average per row, never the total, so this is the only way to weigh it by pig count.
            // Both figures are null together exactly when the row is not yet settled - the same
            // incomplete case the two weight-pair averages above flag with `*`.
            entries.avg_discarded_weight = averageText(
                (row) => (row.avg_discarded_weight === null || row.discarded_pigs === null ? null : row.avg_discarded_weight * row.discarded_pigs),
                (row) => row.discarded_pigs
            );
        }

        return entries;
    });

    // `page.totals` is deliberately not re-exported: it is computed over `total_weight`/`avg_weight`,
    // which `mapSlaughterRow` leaves null on every row (see that file), so it would read as a
    // reader-facing "Total weight 0" for a table full of weighed pigs. `totalsSubheader` above is
    // this page's own footer instead.
    const { totals: _unusedSharedTotals, ...pageWithoutTotals } = page;

    return {
        ...pageWithoutTotals,
        // Overrides the shared single-measure bins: the dedicated chart needs the discarded count
        // alongside pig count, which `useRecordsAggregation`'s own bins have no room for.
        bins,
        columns: computed(() => columns(isVisible)),
        columnToggleOptions,
        totalsSubheader,
        toggleColumn,
        setAllColumns,
    };
}
